<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { wishesAPI } from '@/services/api';
import { useRoute, useRouter } from 'vue-router';
import { SoloWishCard, LoadingSpinner } from '@/components';
import { ChevronLeft, ChevronRight } from 'lucide-vue-next';
import { devWishData } from '@/config/devWish';

const route = useRoute();
const router = useRouter();
const wish = ref(null);
const error = ref(null);
const loading = ref(true);
const containerRef = ref(null);

const allWishes = ref([]);
const prevWishId = ref(null);
const nextWishId = ref(null);

async function fetchWish() {
    try {
        loading.value = true;
        const id = route.params.id;
        console.log(`Fetching wish with ID: ${id}`);

        // Fetch current wish
        if (id === '0' || id === 'DEV') {
            wish.value = devWishData;
        } else {
            wish.value = await wishesAPI.getById(id);
        }
        console.log("Fetched wish:", wish.value);
        
        // Fetch all wishes to determine navigation if not already loaded
        if (allWishes.value.length === 0) {
            const response = await wishesAPI.getAllWishes();
            if (response.wishes) {
                // Insert Dev wish at the beginning
                allWishes.value = [devWishData, ...response.wishes];
            }
        }
        
        updateNavigation(id);
        error.value = null;
    } catch (err) {
        console.error("Error fetching wish:", err);
        error.value = "Failed to load wish. Please try again later.";
    } finally {
        loading.value = false;
    }
}

function updateNavigation(currentId) {
    if (!allWishes.value || allWishes.value.length === 0) return;
    
    // Find the current wish index. The route param is often the "number" or "id".
    // Try matching both to be safe
    const currentIndex = allWishes.value.findIndex(w => String(w.id) === String(currentId) || String(w.number) === String(currentId));
    
    if (currentIndex !== -1) {
        // Previous is the newer one (lower index in descending order array)
        if (currentIndex > 0) {
            prevWishId.value = allWishes.value[currentIndex - 1].number || allWishes.value[currentIndex - 1].id;
        } else {
            prevWishId.value = null;
        }
        
        // Next is the older one (higher index)
        if (currentIndex < allWishes.value.length - 1) {
            nextWishId.value = allWishes.value[currentIndex + 1].number || allWishes.value[currentIndex + 1].id;
        } else {
            nextWishId.value = null;
        }
    }
}

function navigateTo(id) {
    if (id) {
        router.push(`/wish/${id}`);
    }
}

function handleKeydown(e) {
    // Make sure we aren't in an input field (though there shouldn't be one here)
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

    if (e.key === 'ArrowLeft' && prevWishId.value !== null) {
        navigateTo(prevWishId.value);
    } else if (e.key === 'ArrowRight' && nextWishId.value !== null) {
        navigateTo(nextWishId.value);
    }
}

watch(() => route.params.id, (newId) => {
    if (newId) fetchWish();
});

onMounted(() => {
    fetchWish();
    window.addEventListener('keydown', handleKeydown);
    // Optional: focus container so key events don't rely on document body
    if (containerRef.value) {
        containerRef.value.focus();
    }
});

onUnmounted(() => {
    window.removeEventListener('keydown', handleKeydown);
});
</script>
<template>
    <div 
        ref="containerRef"
        class="relative min-h-[80vh] flex flex-col items-center justify-center p-4 outline-none"
        tabindex="0"
    >
        <LoadingSpinner v-if="loading" />
        <p v-else-if="error" class="text-center text-red-400/80 crystal-card py-6 max-w-md mx-auto">{{ error }}</p>
        
        <div v-else class="w-full max-w-3xl relative">
            <!-- Top Navigation -->
            <div class="flex justify-between items-center w-full mb-6">
                <button 
                    v-if="prevWishId"
                    @click="navigateTo(prevWishId)"
                    class="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-nirvana-lotus/10 border border-nirvana-lotus/30 text-nirvana-lotus hover:bg-nirvana-lotus hover:text-white transition-all text-sm font-medium shadow-lg shadow-nirvana-lotus/5"
                >
                    <ChevronLeft class="w-4 h-4" />
                    ก่อนหน้า
                </button>
                <div v-else></div> <!-- Spacer -->
                
                <button 
                    v-if="nextWishId"
                    @click="navigateTo(nextWishId)"
                    class="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-nirvana-cyan/10 border border-nirvana-cyan/30 text-nirvana-cyan hover:bg-nirvana-cyan hover:text-white transition-all text-sm font-medium shadow-lg shadow-nirvana-cyan/5"
                >
                    ถัดไป
                    <ChevronRight class="w-4 h-4" />
                </button>
            </div>

            <SoloWishCard :wish="wish" />
            
            <div class="text-center mt-6 text-nirvana-silver/30 text-xs hidden md:block">
                ใช้ปุ่มลูกศร <kbd class="px-1.5 py-0.5 rounded bg-white/5 border border-white/10 mx-1">←</kbd> <kbd class="px-1.5 py-0.5 rounded bg-white/5 border border-white/10 mx-1">→</kbd> เพื่อเปลี่ยนหน้าได้น้า
            </div>
        </div>
    </div>
</template>
